import React from 'react';
import { withRouter } from 'react-router';
import SEOHead from '../../components/seo/seo-head';
import { SEO_CONFIG } from '../../constants/seo-config';
import braintreeDropIn from 'braintree-web-drop-in';
import Api from '../../api/api';
import authAPI from '../../api/authApi';
import threedstatuses from '../../components/upgrade/threedsecureresponses';
import PostHogService from '../../services/posthog';
import PostHogEvents from '../../constants/posthog-events';

const HIGHLIGHTS = [
  { icon: '🧠', title: 'Historical to Cutting-Edge', desc: 'From the historical foundations to the frontiers of brain science — all in one compelling narrative.' },
  { icon: '🔬', title: 'Complex Topics Made Accessible', desc: 'Migraine, epilepsy, dementia and more — unravelled with scientific rigour and engaging storytelling.' },
  { icon: '📖', title: 'For Everyone', desc: 'Equally valuable for general readers, healthcare professionals, and students exploring the fringes of neurology.' },
];

class MarketingSurfingTheBrain extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: false,
      error_messages: [],
      loading: false,
      success: null,
      errorMessage: false,
    };
    this.captcha = Api.captcha();
    this._dropinCallback = this._dropinCallback.bind(this);
    this._submit = this._submit.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    PostHogService.capture(PostHogEvents.BLOG_PAGE_OPENED);
    this.initReveal();

    authAPI.getBookPaymentToken(this.props.userApiRequest, {
      onSuccess: ({ data }) => {
        const clientToken = data.client_token;
        braintreeDropIn.create(
          {
            authorization: clientToken,
            threeDSecure: true,
            container: '#bt-dropin',
            paypal: { flow: 'vault' },
          },
          this._dropinCallback
        );
      },
      onError: () => {},
    });
  }

  componentDidUpdate() {
    this.initReveal();
  }

  initReveal() {
    const reveals = document.querySelectorAll('.reveal:not(.visible)');
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            io.unobserve(e.target);
          }
        });
      }, { threshold: 0.12 });
      reveals.forEach(el => io.observe(el));
    } else {
      reveals.forEach(el => el.classList.add('visible'));
    }
  }

  _dropinCallback(createErr, instance) {
    if (createErr) {
      if ('Rollbar' in window) Rollbar.error('Dropin could not be created', createErr);
      return;
    }
    this.braintreeInstance = instance;
    this.setState({ loading: false, errorMessage: false });
  }

  handleSubmit(e) {
    e.preventDefault();
    if (!this.braintreeInstance) return;

    let data = {
      first_name: this.refs.first_name.value,
      last_name: this.refs.last_name.value,
      email: this.refs.email.value,
      address_1: this.refs.address_1.value,
      address_2: this.refs.address_2.value,
      city: this.refs.city.value,
      region: this.refs.region.value,
      country: this.refs.country.value,
      postcode: this.refs.postcode.value,
      captcha: this.refs.captcha.value
    };

    let error_messages = this.validateInput(data);
    if (error_messages.length) {
      this.setState({ loading: false, error: true, error_messages, errorMessage: false });
      return;
    }

    this.setState({ errorMessage: false, error: false, error_messages: [], loading: true });

    this.braintreeInstance.requestPaymentMethod(
      {
        threeDSecure: {
          amount: 9.99,
          email: this.refs.email.value,
        },
      },
      (requestPaymentMethodErr, payload) => {
        if (requestPaymentMethodErr) {
          console.error(requestPaymentMethodErr);
          this.setState({ loading: false, errorMessage: requestPaymentMethodErr.message || 'Please complete your payment details.' });
          return;
        }
        if (payload.type === 'CreditCard') {
          let statusDetails = threedstatuses[payload.threeDSecureInfo.status];
          if (statusDetails.continue === false) {
            this.setState({
              loading: false,
              errorMessage: statusDetails.retry
                ? 'A temporary 3DSecure error has occurred. Please retry.'
                : 'We have been unable to verify your card with 3DSecure, please use a different payment method.',
            });
            return;
          }
          this._submit(payload.nonce, payload.threeDSecureInfo.threeDSecureAuthenticationId);
        } else {
          this._submit(payload.nonce);
        }
      }
    );
  }

  _submit(nonce, threedsecure) {
    PostHogService.capture(PostHogEvents.SURFING_BRAIN_PURCHASE_STARTED);
    let data = {
      payment_method_nonce: nonce,
      first_name: this.refs.first_name.value,
      last_name: this.refs.last_name.value,
      email: this.refs.email.value,
      address_1: this.refs.address_1.value,
      address_2: this.refs.address_2.value,
      city: this.refs.city.value,
      region: this.refs.region.value,
      country: this.refs.country.value,
      postcode: this.refs.postcode.value,
    };

    const captcha = this.refs.captcha.value;
    this.setState({ loading: true });

    let error_messages = this.validateInput(Object.assign({}, data, { captcha }));
    if (error_messages.length) {
      this.setState({ loading: false, error: true, error_messages });
      return;
    }

    if (threedsecure) data['three_d_secure_authentication_id'] = threedsecure;

    authAPI.sendBookPaymentDetails(this.props.userApiRequest, data, {
      onSuccess: ({ data }) => {
        if (!data.error) {
          this.setState({ loading: false, success: true });
          PostHogService.capture(PostHogEvents.SURFING_BRAIN_PURCHASE_SUCCESS, { amount: 9.99 });
        }
      },
      onError: e => {
        if ('Rollbar' in window) Rollbar.error('Payment error occurred', e);
        this.setState({ error: true, success: false, loading: false });
      },
    });
  }

  validateInput(data) {
    const requiredFields = {
      first_name: 'First name',
      last_name: 'Last name',
      email: 'Email',
      address_1: 'Address 1',
      city: 'City',
      country: 'Country',
      postcode: 'Postcode/Zip',
    };
    const error_messages = [];

    if (+data.captcha != this.captcha.result) {
      error_messages.push('Your answer was incorrect, please try again');
    }
    for (const [field, label] of Object.entries(requiredFields)) {
      if (data[field] === '') error_messages.push(`${label} missing`);
    }
    return error_messages;
  }

  render() {
    const { success, loading, errorMessage, error, error_messages } = this.state;

    return (
      <div className="new-homepage-wrapper">
        <SEOHead {...SEO_CONFIG.surfingTheBrain} />

        {/* Hero */}
        <div className="section" style={{ paddingTop: 112, textAlign: 'center' }}>
          <div className="eyebrow reveal" style={{ justifyContent: 'center' }}>
            <div className="eyebrow-line"></div>
            <span className="eyebrow-text">New Book</span>
            <div className="eyebrow-line"></div>
          </div>
          <h1 className="section-title reveal reveal-delay-1">
            Surfing the Brain<br /><em>An exploration of the fringes of Neurology</em>
          </h1>
          <p className="section-sub reveal reveal-delay-2">
            From the historical foundations to the cutting-edge frontiers of brain science — with stories,
            discoveries and breakthroughs that shaped modern neurology.
          </p>
        </div>

        {/* Book + description */}
        <div className="section" style={{ paddingTop: 0 }}>
          <div className="cap-row reveal">
            <div className="cap-visual" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                src={require('./surfing-the-brain.jpg')}
                alt="Surfing the Brain book cover"
                style={{ maxWidth: 320, width: '100%', borderRadius: 12, boxShadow: '0 12px 48px rgba(0,0,0,.18)' }}
              />
            </div>
            <div>
              <div className="cap-eyebrow">About the book</div>
              <h2 className="cap-title">Where science meets <em>storytelling</em></h2>
              <p className="cap-desc">
                Dive into the fascinating world of neurology with <em>Surfing the Brain</em> — a compelling exploration
                that unravels complex neurological disorders like migraine, epilepsy, and dementia, while uncovering
                intriguing phenomena at their peripheries.
              </p>
              <p className="cap-desc" style={{ marginTop: 12 }}>
                Through engaging narratives, discover strange research findings, unexpected discoveries, and the evolution
                of neurological practices. The author masterfully weaves together stories of pioneering figures, landmark
                cases, and technological breakthroughs.
              </p>
              <p className="cap-desc" style={{ marginTop: 12 }}>
                Accessible to general readers, yet a valuable resource for healthcare professionals and students offering
                insights into rarely discussed aspects of neurology.
              </p>
              <a href="#get-your-copy" className="cap-link" style={{ marginTop: 24 }}>
                Get your copy for £9.99 →
              </a>
            </div>
          </div>
        </div>

        {/* Highlights */}
        <div className="section" style={{ paddingTop: 0 }}>
          <div className="feature-grid">
            {HIGHLIGHTS.map((h, i) => (
              <div className={`feature-card reveal reveal-delay-${i + 1}`} key={i}>
                <div className="feature-icon">{h.icon}</div>
                <div className="feature-title">{h.title}</div>
                <div className="feature-desc">{h.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Purchase form */}
        <div className="steps-section" id="get-your-copy">
          <div className="steps-grid-bg"></div>
          <div className="steps-inner" style={{ maxWidth: 640 }}>
            <div className="eyebrow reveal" style={{ justifyContent: 'center' }}>
              <div className="eyebrow-line" style={{ background: 'rgba(147,197,253,.5)' }}></div>
              <span className="eyebrow-text">Order your copy</span>
              <div className="eyebrow-line" style={{ background: 'rgba(147,197,253,.5)' }}></div>
            </div>
            <h2 className="section-title reveal reveal-delay-1" style={{ textAlign: 'center' }}>
              Neurochecklists price: <em>£9.99</em>
            </h2>

            {success ? (
              <p style={{ textAlign: 'center', color: '#10b981', fontSize: 16, marginTop: 24 }}>
                ✓ Your order has been placed. Thank you!
              </p>
            ) : (
              <form onSubmit={this.handleSubmit} style={{ marginTop: 32 }}>
                {error && error_messages.length > 0 && (
                  <div style={{ background: 'rgba(248,113,113,.1)', border: '1px solid rgba(248,113,113,.3)', borderRadius: 8, padding: '14px 18px', marginBottom: 20 }}>
                    <ul style={{ margin: 0, paddingLeft: 18, color: '#F87171', fontSize: 14 }}>
                      {error_messages.map((msg, i) => <li key={i}>{msg}</li>)}
                    </ul>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="input-group">
                    <label className="input-label" style={{ color: 'rgba(255,255,255,.7)' }}>First Name <span style={{ color: '#F87171' }}>*</span></label>
                    <input className="input-field" type="text" ref="first_name" name="first_name" autoComplete="given-name" required disabled={loading} style={{ background: 'rgba(255,255,255,.06)', color: '#fff', borderColor: 'rgba(255,255,255,.12)' }} />
                  </div>
                  <div className="input-group">
                    <label className="input-label" style={{ color: 'rgba(255,255,255,.7)' }}>Surname <span style={{ color: '#F87171' }}>*</span></label>
                    <input className="input-field" type="text" ref="last_name" name="last_name" autoComplete="family-name" required disabled={loading} style={{ background: 'rgba(255,255,255,.06)', color: '#fff', borderColor: 'rgba(255,255,255,.12)' }} />
                  </div>
                </div>

                <div className="input-group" style={{ marginTop: 16 }}>
                  <label className="input-label" style={{ color: 'rgba(255,255,255,.7)' }}>Email <span style={{ color: '#F87171' }}>*</span></label>
                  <input className="input-field" type="email" ref="email" name="email" autoComplete="email" required disabled={loading} style={{ background: 'rgba(255,255,255,.06)', color: '#fff', borderColor: 'rgba(255,255,255,.12)' }} />
                </div>

                <div className="input-group" style={{ marginTop: 16 }}>
                  <label className="input-label" style={{ color: 'rgba(255,255,255,.7)' }}>Address line 1 <span style={{ color: '#F87171' }}>*</span></label>
                  <input className="input-field" type="text" ref="address_1" name="address_1" autoComplete="address-line1" required disabled={loading} style={{ background: 'rgba(255,255,255,.06)', color: '#fff', borderColor: 'rgba(255,255,255,.12)' }} />
                </div>

                <div className="input-group" style={{ marginTop: 16 }}>
                  <label className="input-label" style={{ color: 'rgba(255,255,255,.7)' }}>Address line 2</label>
                  <input className="input-field" type="text" ref="address_2" name="address_2" autoComplete="address-line2" disabled={loading} style={{ background: 'rgba(255,255,255,.06)', color: '#fff', borderColor: 'rgba(255,255,255,.12)' }} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
                  <div className="input-group">
                    <label className="input-label" style={{ color: 'rgba(255,255,255,.7)' }}>Town / City <span style={{ color: '#F87171' }}>*</span></label>
                    <input className="input-field" type="text" ref="city" name="city" autoComplete="address-level2" required disabled={loading} style={{ background: 'rgba(255,255,255,.06)', color: '#fff', borderColor: 'rgba(255,255,255,.12)' }} />
                  </div>
                  <div className="input-group">
                    <label className="input-label" style={{ color: 'rgba(255,255,255,.7)' }}>Region</label>
                    <input className="input-field" type="text" ref="region" name="region" autoComplete="address-level1" disabled={loading} style={{ background: 'rgba(255,255,255,.06)', color: '#fff', borderColor: 'rgba(255,255,255,.12)' }} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
                  <div className="input-group">
                    <label className="input-label" style={{ color: 'rgba(255,255,255,.7)' }}>Country <span style={{ color: '#F87171' }}>*</span></label>
                    <input className="input-field" type="text" ref="country" name="country" autoComplete="country-name" required disabled={loading} style={{ background: 'rgba(255,255,255,.06)', color: '#fff', borderColor: 'rgba(255,255,255,.12)' }} />
                  </div>
                  <div className="input-group">
                    <label className="input-label" style={{ color: 'rgba(255,255,255,.7)' }}>Zip / Postcode <span style={{ color: '#F87171' }}>*</span></label>
                    <input className="input-field" type="text" ref="postcode" name="postcode" autoComplete="postal-code" required disabled={loading} style={{ background: 'rgba(255,255,255,.06)', color: '#fff', borderColor: 'rgba(255,255,255,.12)' }} />
                  </div>
                </div>

                <div className="input-group" style={{ marginTop: 16 }}>
                  <label className="input-label" style={{ color: 'rgba(255,255,255,.7)' }}>
                    What is the sum of {this.captcha.leftside} + {this.captcha.rightside}? <span style={{ color: '#F87171' }}>*</span>
                  </label>
                  <input className="input-field" type="text" ref="captcha" required disabled={loading} style={{ background: 'rgba(255,255,255,.06)', color: '#fff', borderColor: 'rgba(255,255,255,.12)' }} />
                </div>

                <div id="bt-dropin" style={{ marginTop: 24, opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto' }}></div>

                {errorMessage && (
                  <p style={{ color: '#FBBF24', fontSize: 14, marginTop: 12 }}>{errorMessage}</p>
                )}

                <button
                  type="button"
                  onClick={this.handleSubmit}
                  ref="purchase"
                  disabled={loading}
                  className="btn btn-hero-primary"
                  style={{ marginTop: 24, width: '100%', justifyContent: 'center', fontSize: 15, padding: '14px 28px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
                >
                  {loading ? 'Processing Payment...' : 'Get Surfing the Brain for £9.99 →'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(MarketingSurfingTheBrain);
